import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as AssignIcon,
  PersonRemove as VacateIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  Construction as BuildIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import roomService from '../services/roomService';
import tenantService from '../services/tenantService';
import { PageHeader, Loading, EmptyState } from '../components/Layout';

const Rooms = () => {
  const [rooms, setRooms] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [assigningRoom, setAssigningRoom] = useState(null);
  const [formData, setFormData] = useState({
    room_number: '',
    property_name: '',
    monthly_rent: '',
    security_deposit: '',
    room_type: '',
    floor_number: '',
    amenities: '',
  });
  const [selectedTenant, setSelectedTenant] = useState('');
  const [depositAmount, setDepositAmount] = useState('');

  // Common room types in the Kenyan market
  const roomTypes = [
    'Bedsitter',
    'Single Room',
    '1 Bedroom',
    '2 Bedroom',
    '3 Bedroom',
    '4 Bedroom',
    'Studio',
    'Bachelor Pad',
    'Servant Quarter (SQ)',
    'Double Room',
    'Master Ensuite',
    'Penthouse',
    'Maisonette',
    'Apartment',
    'Bungalow',
  ];

  useEffect(() => {
    fetchRooms();
    fetchTenants();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await roomService.getAllRooms();
      setRooms(response.data);
    } catch (error) {
      toast.error('Failed to load rooms');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await tenantService.getAllTenants();
      setTenants(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const handleOpenDialog = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        room_number: room.room_number,
        property_name: room.property_name,
        monthly_rent: room.monthly_rent,
        security_deposit: room.security_deposit || '',
        room_type: room.room_type || '',
        floor_number: room.floor_number || '',
        amenities: room.amenities || '',
      });
    } else {
      setEditingRoom(null);
      setFormData({
        room_number: '',
        property_name: '',
        monthly_rent: '',
        security_deposit: '',
        room_type: '',
        floor_number: '',
        amenities: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRoom(null);
  };

  const handleOpenAssignDialog = (room) => {
    setAssigningRoom(room);
    setSelectedTenant('');
    setDepositAmount(room.security_deposit ? String(room.security_deposit) : '');
    setOpenAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setOpenAssignDialog(false);
    setAssigningRoom(null);
    setSelectedTenant('');
    setDepositAmount('');
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        monthly_rent: parseFloat(formData.monthly_rent),
        security_deposit: formData.security_deposit ? parseFloat(formData.security_deposit) : null,
        floor_number: formData.floor_number || null,
      };

      if (editingRoom) {
        await roomService.updateRoom(editingRoom.id, data);
        toast.success('Room updated successfully');
      } else {
        await roomService.createRoom(data);
        toast.success('Room created successfully');
      }
      handleCloseDialog();
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
      console.error(error);
    }
  };

  const handleAssignTenant = async () => {
    if (!selectedTenant) {
      toast.error('Please select a tenant');
      return;
    }

    try {
      const assignData = { 
        tenant_id: selectedTenant,
        deposit_amount: depositAmount ? parseFloat(depositAmount) : null
      };
      await roomService.assignRoom(assigningRoom.id, assignData);
      toast.success('Tenant assigned successfully');
      handleCloseAssignDialog();
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign tenant');
      console.error(error);
    }
  };

  const handleVacateRoom = async (roomId) => {
    if (window.confirm('Are you sure you want to vacate this room?')) {
      try {
        await roomService.vacateRoom(roomId);
        toast.success('Room vacated successfully');
        fetchRooms();
      } catch (error) {
        toast.error('Failed to vacate room');
        console.error(error);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this room?')) {
      try {
        await roomService.deleteRoom(id);
        toast.success('Room deleted successfully');
        fetchRooms();
      } catch (error) {
        toast.error('Failed to delete room');
        console.error(error);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'vacant':
        return 'success';
      case 'occupied':
        return 'primary';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'vacant':
        return <CheckCircleIcon fontSize="small" />;
      case 'occupied':
        return <HomeIcon fontSize="small" />;
      case 'maintenance':
        return <BuildIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const vacantRooms = rooms.filter(r => r.status === 'vacant').length;
  const occupiedRooms = rooms.filter(r => r.status === 'occupied').length;

  return (
    <Box>
      <PageHeader
        title="Rooms"
        subtitle="Manage your property rooms and assignments"
        breadcrumbs={[{ label: 'Rooms' }]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Room
          </Button>
        }
      />

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Total Rooms
                  </Typography>
                  <Typography variant="h4">{rooms.length}</Typography>
                </Box>
                <HomeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Vacant
                  </Typography>
                  <Typography variant="h4">{vacantRooms}</Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="h6">
                    Occupied
                  </Typography>
                  <Typography variant="h4">{occupiedRooms}</Typography>
                </Box>
                <HomeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading ? (
        <Loading message="Loading rooms..." />
      ) : rooms.length === 0 ? (
        <EmptyState
          title="No rooms yet"
          description="Get started by adding your first room to the system."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Your First Room
            </Button>
          }
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Room Number</strong></TableCell>
                <TableCell><strong>Property</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Rent (KES)</strong></TableCell>
                <TableCell><strong>Floor</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Current Tenant</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id}>
                  <TableCell>{room.room_number}</TableCell>
                  <TableCell>{room.property_name}</TableCell>
                  <TableCell>{room.room_type || 'N/A'}</TableCell>
                  <TableCell>{room.monthly_rent?.toLocaleString()}</TableCell>
                  <TableCell>{room.floor_number || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(room.status)}
                      label={room.status?.toUpperCase()}
                      color={getStatusColor(room.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {room.current_tenant_name || '-'}
                  </TableCell>
                  <TableCell align="right">
                    {room.status === 'vacant' && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenAssignDialog(room)}
                        title="Assign Tenant"
                      >
                        <AssignIcon />
                      </IconButton>
                    )}
                    {room.status === 'occupied' && (
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleVacateRoom(room.id)}
                        title="Vacate Room"
                      >
                        <VacateIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(room)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(room.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Room Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRoom ? 'Edit Room' : 'Add New Room'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Room Number"
            name="room_number"
            value={formData.room_number}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Property Name"
            name="property_name"
            value={formData.property_name}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Monthly Rent (KES)"
            name="monthly_rent"
            type="number"
            value={formData.monthly_rent}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Security Deposit (KES)"
            name="security_deposit"
            type="number"
            value={formData.security_deposit}
            onChange={handleInputChange}
            margin="normal"
            helperText="Required deposit amount for this room. An SDP invoice will be auto-generated when a tenant is assigned."
          />
          <TextField
            fullWidth
            select
            label="Room Type"
            name="room_type"
            value={formData.room_type}
            onChange={handleInputChange}
            margin="normal"
            required
            helperText="Select the type of room/unit"
          >
            <MenuItem value="">
              <em>Select Room Type</em>
            </MenuItem>
            {roomTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Floor Number"
            name="floor_number"
            value={formData.floor_number}
            onChange={handleInputChange}
            margin="normal"
            placeholder="e.g., Ground Floor, First Floor, Second Floor"
            helperText="Enter the floor name or number"
          />
          <TextField
            fullWidth
            label="Amenities"
            name="amenities"
            value={formData.amenities}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={2}
            placeholder="e.g., WiFi, Water, Parking"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingRoom ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Tenant Dialog */}
      <Dialog open={openAssignDialog} onClose={handleCloseAssignDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Tenant to Room {assigningRoom?.room_number}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Select Tenant"
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            margin="normal"
            required
          >
            {tenants.length === 0 ? (
              <MenuItem value="">No available tenants</MenuItem>
            ) : (
              tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.full_name} - {tenant.email}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            fullWidth
            label="Security Deposit Amount (KES)"
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            margin="normal"
            placeholder="0"
            helperText={`A security deposit invoice (SDP-...) will be auto-generated for this amount.${assigningRoom?.security_deposit ? ` Room default: KES ${Number(assigningRoom.security_deposit).toLocaleString()}` : ''}`}
          />
          {tenants.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please create a tenant first before assigning to a room.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button onClick={handleAssignTenant} variant="contained" disabled={tenants.length === 0}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Rooms;
